import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { AlertCircle, Save } from 'react-feather';
import {
  CAMPOS_IMPORTE,
  calcularCambios,
  formatearImporte,
  valoresIniciales,
} from '../../features/precios';
import colors from '../../theme/colors';

const FORM_ID = 'form-editar-precio';

const ModalEditarPrecio = ({
  isOpen,
  fila = null,
  guardando = false,
  errorGuardado = '',
  onGuardar,
  onCerrar,
  onClosed = undefined,
}) => {
  // El padre monta el modal con key por fila: el estado inicial sale de la fila
  // sin un efecto de sincronizacion (que pintaria un frame con errores).
  const [valores, setValores] = useState(() => valoresIniciales(fila));

  const { cambios, errores } = useMemo(() => calcularCambios(fila, valores), [fila, valores]);
  const hayErrores = Object.keys(errores).length > 0;
  const hayCambios = Object.keys(cambios).length > 0;

  const cerrar = () => {
    if (!guardando) onCerrar();
  };

  const enviar = (e) => {
    e.preventDefault();
    if (guardando || hayErrores || !hayCambios) return;
    onGuardar(cambios);
  };

  return (
    <Modal isOpen={isOpen} toggle={cerrar} onClosed={onClosed} centered size="md">
      <ModalHeader toggle={cerrar}>Editar precio</ModalHeader>
      <ModalBody>
        {fila && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontWeight: 600, color: colors.text }}>{fila.curso || fila.codigocurso}</div>
            <div style={{ fontSize: '0.8rem', color: colors.textSecondary }}>
              {fila.codigocurso} · {fila.centro} · {fila.tipo}
            </div>
          </div>
        )}
        <Form id={FORM_ID} onSubmit={enviar} noValidate>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              columnGap: '16px',
            }}
          >
            {CAMPOS_IMPORTE.map(({ key, label }) => {
              const id = `precio-${key}`;
              const error = errores[key];
              return (
                <FormGroup key={key}>
                  <Label for={id} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {label} (€)
                  </Label>
                  <Input
                    id={id}
                    name={key}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={valores[key] ?? ''}
                    onChange={(e) => {
                      const { value } = e.target;
                      setValores((prev) => ({ ...prev, [key]: value }));
                    }}
                    invalid={Boolean(error)}
                    disabled={guardando}
                    aria-describedby={`${id}-actual`}
                  />
                  {error && <FormFeedback>{error}</FormFeedback>}
                  <div
                    id={`${id}-actual`}
                    style={{ fontSize: '0.75rem', color: colors.textSecondary, marginTop: '2px' }}
                  >
                    Actual: {formatearImporte(fila?.[key])}
                  </div>
                </FormGroup>
              );
            })}
          </div>
        </Form>
        {errorGuardado && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '10px 12px',
              borderRadius: '8px',
              background: colors.dangerLight,
              color: colors.dangerDark,
              fontSize: '0.85rem',
            }}
          >
            <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>{errorGuardado}</span>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={cerrar} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          color="primary"
          type="submit"
          form={FORM_ID}
          disabled={guardando || hayErrores || !hayCambios}
        >
          {guardando ? <Spinner size="sm" /> : <Save size={16} aria-hidden="true" />}{' '}
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ModalEditarPrecio.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  fila: PropTypes.shape({
    idcentro: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    centro: PropTypes.string,
    codigocurso: PropTypes.string,
    curso: PropTypes.string,
    tipo: PropTypes.string,
  }),
  guardando: PropTypes.bool,
  errorGuardado: PropTypes.string,
  onGuardar: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
  onClosed: PropTypes.func,
};

export default ModalEditarPrecio;
